import { createSelector } from '@ngrx/store';
import type { SettingsPane } from '../analytics/analytics.events';
import { connectorsFeature } from '../connectors/connectors.reducer';
import { featureFlagsFeature } from '../feature-flags';
import { layoutFeature } from '../layout/store/layout.reducer';

/**
 * Whether the Connectors pane, and every way into it, can show (SB-6520 KD-19). `pending` until the
 * signed-in user's flags have loaded (before that the flag may be the anonymous context's), then
 * while the flag is on and the first list load hasn't resolved; `available` once a load succeeded
 * with at least one connector, which a later failed refresh doesn't undo; `unavailable` otherwise.
 */
export type ConnectorsAvailability = 'pending' | 'available' | 'unavailable';

export const selectConnectorsAvailability = createSelector(
  featureFlagsFeature.selectReady,
  featureFlagsFeature.selectFeatureFlag<boolean>('ff-mcp-connectors'),
  connectorsFeature.selectFirstLoad,
  connectorsFeature.selectConnectors,
  (flagsReady, enabled, firstLoad, connectors): ConnectorsAvailability => {
    if (!flagsReady) return 'pending';
    if (!enabled || firstLoad === 'failed') return 'unavailable';
    if (firstLoad === 'pending') return 'pending';
    return connectors.length ? 'available' : 'unavailable';
  },
);

/** Preferences always; Memory behind `ff-user-memory`; Connectors once available. */
export const selectAvailableSettingsPanes = createSelector(
  featureFlagsFeature.selectFeatureFlag<boolean>('ff-user-memory'),
  selectConnectorsAvailability,
  (memory, connectors): SettingsPane[] => [
    'preferences',
    ...(memory ? (['memory'] as const) : []),
    ...(connectors === 'available' ? (['connectors'] as const) : []),
  ],
);

/** The pane on screen: null for the mobile pane list, Preferences when nothing usable is chosen. */
export const selectVisibleSettingsPane = createSelector(
  layoutFeature.selectSettings,
  layoutFeature.selectIsMobile,
  selectAvailableSettingsPanes,
  (settings, isMobile, available): SettingsPane | null => {
    if (settings.selectedPane && available.includes(settings.selectedPane)) {
      return settings.selectedPane;
    }
    return isMobile && settings.selectedPane === null ? null : 'preferences';
  },
);

/**
 * Open, except while a request for Connectors waits on a list that isn't available yet (KD-12).
 * Once they are available the request shows at once, so a sign-in return reaching a window
 * already open on another pane switches panes instead of closing and reopening it (3.5).
 */
export const selectSettingsVisible = createSelector(
  layoutFeature.selectSettings,
  selectConnectorsAvailability,
  (settings, connectors) =>
    settings.isOpen &&
    (!settings.awaitingConnectors || connectors === 'available'),
);
